import React, { useMemo } from "react";
import axiosInstance from "../../services/api/axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FormModal, Form, Field, FormSubmit, AddressFields } from "../../components/form";
import { createCustomerSchema } from "../../services/users/schemas";
import { CUSTOMER_TYPE, CUSTOMER_TYPE_OPTIONS } from "../../constants/enums";

const EMPTY_ADDRESS = {
  buildingNo: "",
  streetName1: "",
  streetName2: "",
  city: "",
  district: "",
  pinCode: "",
  state: "",
  country: "India",
};

// Mirror of services/shared/shared_layer/schemas/subscription.py
// SubscriptionTypeIDEnum. Kept here as a constant rather than fetched from
// /subscriptions/type so the admin form has a deterministic, ordered list
// to render even on a fresh DB.
const SUBSCRIPTION_TYPE_ID = {
  BUILDVIZER_UNIFIED: 1,
  BUILDVIZER_PLUS: 2,
};

const SUBSCRIPTION_TYPE_OPTIONS = [
  {
    value: SUBSCRIPTION_TYPE_ID.BUILDVIZER_UNIFIED,
    label: "BuildVizer Unified (CRM + Interior Designer)",
  },
  {
    value: SUBSCRIPTION_TYPE_ID.BUILDVIZER_PLUS,
    label: "BuildVizer Plus (Interior Designer only)",
  },
];

// Billing frequency for the inventory-based payment plan.
const SUBSCRIPTION_PAYMENT_TYPE_OPTIONS = [
  { value: "MONTHLY", label: "Monthly" },
  { value: "QUARTERLY", label: "Quarterly" },
  { value: "YEARLY", label: "Annual" },
];

const buildDefaults = (existing) => ({
  companyName: existing?.companyName || "",
  ownerName: existing?.ownerName || "",
  mobileNoCalling: existing?.mobileNoCalling || "",
  mobileNoWa: existing?.mobileNoWa || "",
  emailId: existing?.emailId || "",
  customerType: existing?.customerType || CUSTOMER_TYPE.DEVELOPER,
  isActive: existing?.isActive ?? true,
  idDeveloperApprovalRequired: existing?.idDeveloperApprovalRequired ?? false,
  dob: existing?.dob || "",
  address: { ...EMPTY_ADDRESS, ...(existing?.address || {}) },
  // Subscription scaffolding for new customers. Defaults to the full plan
  // (Unified) — the admin can drop to Plus before submitting if the tenant
  // is buying the ID-only tier. Ignored when editing an existing customer
  // since their subscription is managed via the Subscription tab.
  subscriptionTypeId: SUBSCRIPTION_TYPE_ID.BUILDVIZER_UNIFIED,
  // Inventory-based payment plan (charged after creation): one-time upfront +
  // per active-unit cost billed each period at the chosen frequency.
  subscriptionPaymentType: "MONTHLY",
  subscriptionUpfrontAmount: 0,
  subscriptionPerUnitCost: 0,
});

const CustomerAddEditModal = ({ isOpen, onClose, existingCustomer, onFetchCustomerList }) => {
  const isEdit = !!existingCustomer;
  const defaultValues = useMemo(() => buildDefaults(existingCustomer), [existingCustomer]);

  // After creating a customer, seed an initial subscription matching the
  // selected plan. Failure here doesn't roll back the customer create — the
  // admin can retry from the Subscription tab on the customer record.
  const seedInitialSubscription = async (customerId, formData) => {
    const today = new Date();
    const endDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000); // 1-year term by default
    const toYmd = (d) => d.toISOString().split("T")[0];
    try {
      await axiosInstance.post("/subscriptions", {
        customerId,
        subscriptionTypeId: Number(formData?.subscriptionTypeId) || SUBSCRIPTION_TYPE_ID.BUILDVIZER_UNIFIED,
        startDate: toYmd(today),
        endDate: toYmd(endDate),
        price: 0,
        currencyId: 4, // INR
        paymentType: formData?.subscriptionPaymentType || "MONTHLY",
        paymentDetails: "Initial subscription created via Zumale admin",
        // Inventory-based billing inputs.
        upfrontAmount:
          formData?.subscriptionUpfrontAmount != null && formData?.subscriptionUpfrontAmount !== ""
            ? Number(formData.subscriptionUpfrontAmount)
            : null,
        perUnitCost:
          formData?.subscriptionPerUnitCost != null && formData?.subscriptionPerUnitCost !== ""
            ? Number(formData.subscriptionPerUnitCost)
            : null,
      });
    } catch (subErr) {
      console.warn("Failed to seed initial subscription:", subErr);
      toast.warn("Customer created but subscription seed failed — assign one from the Subscription tab.");
    }
  };

  const handleSubmit = async (data) => {
    try {
      // The /customers BE endpoint doesn't accept the subscription scaffolding
      // fields. Strip them out before sending the customer create payload.
      const {
        subscriptionTypeId,
        subscriptionPaymentType,
        subscriptionUpfrontAmount,
        subscriptionPerUnitCost,
        ...customerPayload
      } = data;

      const resp = await axiosInstance.post("/customers", customerPayload);
      const customerId = resp?.customerId || resp?.id;
      if (customerId && !isEdit) {
        await seedInitialSubscription(customerId, {
          subscriptionTypeId,
          subscriptionPaymentType,
          subscriptionUpfrontAmount,
          subscriptionPerUnitCost,
        });
      }
      toast.success(isEdit ? "Customer updated successfully!" : "Customer created successfully!");
      onFetchCustomerList?.();
      onClose?.();
    } catch (err) {
      console.error("Failed to save customer:", err);
      toast.error(
        err?.detail || err?.response?.data?.detail || "Failed to save customer. Please try again.",
      );
    }
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      title={isEdit ? "Update Customer" : "Add New Customer"}
      size="xl"
    >
      <Form
        key={existingCustomer?.customerId ?? "new"}
        schema={createCustomerSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      >
        <h3 className="text-sm font-semibold text-slate-700 mb-2">Customer Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field name="companyName" label="Company Name" placeholder="Enter company name" required />
          <Field name="ownerName" label="Owner Name" placeholder="Enter owner name" required />
          <Field
            name="customerType"
            label="Customer Type"
            type="select"
            options={CUSTOMER_TYPE_OPTIONS}
            required
          />
          <Field
            name="mobileNoCalling"
            label="Calling Phone Number"
            type="phone"
            placeholder="Enter calling phone number"
          />
          <Field
            name="mobileNoWa"
            label="WhatsApp Phone Number"
            type="phone"
            placeholder="Enter WhatsApp phone number"
            required
          />
          <Field
            name="emailId"
            label="Email ID"
            type="email"
            placeholder="Enter email address"
            required
          />
          <Field name="dob" label="Date of Birth" type="date" />
          <Field
            name="idDeveloperApprovalRequired"
            label="Interior Design Developer Approval Required"
            type="checkbox"
          />
        </div>

        <h3 className="text-sm font-semibold text-slate-700 mt-6 mb-2">Address Details</h3>
        <AddressFields prefix="address" />

        {!isEdit && (
          <>
            <h3 className="text-sm font-semibold text-slate-700 mt-6 mb-2">
              Subscription &amp; Payment Plan
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              The customer is seeded with this subscription on creation. Choose the plan and the
              inventory-based payment plan: a one-time upfront amount plus a per-inventory cost
              billed each period (units in active phases × per-inventory cost).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                name="subscriptionTypeId"
                label="Plan"
                type="select"
                options={SUBSCRIPTION_TYPE_OPTIONS}
                required
              />
              <Field
                name="subscriptionPaymentType"
                label="Billing Frequency"
                type="select"
                options={SUBSCRIPTION_PAYMENT_TYPE_OPTIONS}
              />
              <Field
                name="subscriptionUpfrontAmount"
                label="Upfront Amount (INR, one-time)"
                type="number"
                placeholder="0"
              />
              <Field
                name="subscriptionPerUnitCost"
                label="Per Inventory Cost (INR)"
                type="number"
                placeholder="0"
              />
            </div>
          </>
        )}

        {isEdit && (
          <div className="mt-6 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm p-4">
            To change this developer's <strong>subscription / payment plan</strong> or manage{" "}
            <strong>invoices &amp; payment installments and receipts</strong>, open the developer
            from the list (the <em>View</em> / eye icon) and use the <strong>Subscription</strong> and{" "}
            <strong>Installments</strong> tabs.
          </div>
        )}

        <FormSubmit loadingLabel={isEdit ? "Saving..." : "Creating..."}>
          {isEdit ? "Update" : "Create"}
        </FormSubmit>
      </Form>
    </FormModal>
  );
};

export default CustomerAddEditModal;
